import { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext, API } from '@/App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Phone, Lock, Mail, Key } from 'lucide-react';

export default function Auth() {
  const navigate = useNavigate();
  const { login } = useContext(AuthContext);
  const [isLoading, setIsLoading] = useState(false);
  
  // Email login state
  const [emailLoginData, setEmailLoginData] = useState({ email: '', password: '' });
  
  // OTP login state
  const [otpStep, setOtpStep] = useState('phone'); // phone, otp
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otp, setOtp] = useState('');

  // Email/Password Login
  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, emailLoginData);
      login(response.data.token, response.data.user);
      toast.success('Login successful!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  // Mobile OTP Login
  const handleSendOTP = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/auth/send-otp`, {
        phone_number: phoneNumber
      });

      toast.success(`OTP sent: ${response.data.otp}`, { duration: 10000 });
      setOtpStep('otp');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/auth/verify-otp`, {
        phone_number: phoneNumber,
        otp_code: otp
      });

      login(response.data.token, response.data.user);
      toast.success('Login successful!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid OTP');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/auth/send-otp`, { phone_number: phoneNumber });
      toast.success(`OTP resent: ${response.data.otp}`, { duration: 10000 });
    } catch (error) {
      toast.error('Failed to resend OTP');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{backgroundColor: '#000000'}}>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(0,255,102,0.1),transparent_50%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(0,255,102,0.1),transparent_50%)]" />
      
      <Card className="relative w-full max-w-md p-8 shadow-2xl" style={{backgroundColor: '#1a1a1a', borderColor: '#00FF66', borderWidth: '2px'}} data-testid="auth-card">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2" style={{fontFamily: 'Space Grotesk, sans-serif', color: '#00FF66'}}>TurfSense</h1>
          <p style={{fontFamily: 'Inter, sans-serif', color: '#FFFFFF'}}>Smart Ground Management</p>
        </div>

        <Tabs defaultValue="otp" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-6" style={{backgroundColor: '#000'}}>
            <TabsTrigger value="otp" data-testid="otp-tab" style={{color: '#FFF'}}>Mobile OTP</TabsTrigger>
            <TabsTrigger value="email" data-testid="email-tab" style={{color: '#FFF'}}>Email Login</TabsTrigger>
          </TabsList>

          {/* Mobile OTP Login */}
          <TabsContent value="otp">
            {otpStep === 'phone' ? (
              <form onSubmit={handleSendOTP} className="space-y-4">
                <div>
                  <Label style={{color: '#FFFFFF'}}>Mobile Number</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-3 h-4 w-4" style={{color: '#00FF66'}} />
                    <div className="flex">
                      <div className="flex items-center px-3 py-2 rounded-l-lg" style={{backgroundColor: '#1a1a1a', borderTop: '1px solid #333', borderLeft: '1px solid #333', borderBottom: '1px solid #333', color: '#888'}}>
                        +91
                      </div>
                      <Input
                        type="tel"
                        placeholder="8128365535"
                        className="pl-3 rounded-l-none"
                        style={{backgroundColor: '#000', borderColor: '#333', color: '#FFF'}}
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, '').slice(0, 10))}
                        required
                        maxLength={10}
                        data-testid="phone-input"
                      />
                    </div>
                  </div>
                  <p className="text-xs mt-2" style={{color: '#888'}}>Enter 10-digit mobile number (India)</p>
                </div>

                <Button
                  type="submit"
                  className="w-full font-bold"
                  style={{backgroundColor: '#00FF66', color: '#000'}}
                  disabled={isLoading}
                  data-testid="send-otp-btn"
                >
                  {isLoading ? 'Sending OTP...' : 'Send OTP'}
                </Button>
              </form>
            ) : (
              <form onSubmit={handleVerifyOTP} className="space-y-4">
                <div>
                  <Label style={{color: '#FFFFFF'}}>Enter OTP</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 h-4 w-4" style={{color: '#00FF66'}} />
                    <Input
                      type="text"
                      placeholder="000000"
                      className="pl-10 text-center text-2xl tracking-widest"
                      style={{backgroundColor: '#000', borderColor: '#333', color: '#00FF66'}}
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      maxLength={6}
                      required
                      data-testid="otp-input"
                    />
                  </div>
                  <p className="text-xs mt-2" style={{color: '#888'}}>Sent to {phoneNumber}</p>
                </div>

                <Button
                  type="submit"
                  className="w-full font-bold"
                  style={{backgroundColor: '#00FF66', color: '#000'}}
                  disabled={isLoading || otp.length !== 6}
                  data-testid="verify-otp-btn"
                >
                  {isLoading ? 'Verifying...' : 'Verify & Login'}
                </Button>
                
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    style={{borderColor: '#333', color: '#00FF66'}}
                    onClick={() => { setOtpStep('phone'); setOtp(''); }}
                  >
                    Change Number
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    style={{borderColor: '#333', color: '#00FF66'}}
                    onClick={handleResendOTP}
                    disabled={isLoading}
                  >
                    Resend OTP
                  </Button>
                </div>
              </form>
            )}
          </TabsContent>

          {/* Email/Password Login */}
          <TabsContent value="email">
            <form onSubmit={handleEmailLogin} className="space-y-4">
              <div>
                <Label style={{color: '#FFFFFF'}}>Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4" style={{color: '#00FF66'}} />
                  <Input
                    type="email"
                    placeholder="your@email.com"
                    className="pl-10"
                    style={{backgroundColor: '#000', borderColor: '#333', color: '#FFF'}}
                    value={emailLoginData.email}
                    onChange={(e) => setEmailLoginData({...emailLoginData, email: e.target.value})}
                    required
                    data-testid="email-input"
                  />
                </div>
              </div>

              <div>
                <Label style={{color: '#FFFFFF'}}>Password</Label>
                <div className="relative">
                  <Key className="absolute left-3 top-3 h-4 w-4" style={{color: '#00FF66'}} />
                  <Input
                    type="password"
                    placeholder="••••••••"
                    className="pl-10"
                    style={{backgroundColor: '#000', borderColor: '#333', color: '#FFF'}}
                    value={emailLoginData.password}
                    onChange={(e) => setEmailLoginData({...emailLoginData, password: e.target.value})}
                    required
                    data-testid="password-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full font-bold"
                style={{backgroundColor: '#00FF66', color: '#000'}}
                disabled={isLoading}
                data-testid="email-login-btn"
              >
                {isLoading ? 'Logging in...' : 'Login'}
              </Button>
            </form>
          </TabsContent>
        </Tabs>

        <div className="mt-6 text-center">
          <Button variant="link" onClick={() => navigate('/')} style={{color: '#00FF66'}}>
            Back to Home
          </Button>
        </div>
      </Card>
    </div>
  );
}